import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Minus, Plus, Trash2, Loader2 } from 'lucide-react'
import { cartLineOf, useStore } from '../store/useStore'
import { useI18n } from '../i18n/useI18n'
import { api } from '../services/api'
import { getGuestId, getOrCreateGuestId } from '../lib/guestId'
import { showToast } from './Toast'
import { itemNameLines } from '../lib/itemName'
import { toOrderItems } from '../lib/cartPayload'
import VariationLine from './VariationLine'
import { useCartRejectionRecovery } from '../hooks/useCartRejectionRecovery'

interface CartSheetProps {
  open: boolean
  /** #2039 注文が付く先の表記。バインダー形式の店では「テーブル番号: 3」ではなく「伝票 3」 */
  seatLabel?: string
  onClose: () => void
  /** Called when the "注文する" button sends the cart to the kitchen.
   * Omit together with `hideOrderButton` when the sheet is used as a
   * read-only review step (e.g. delivery mode, where checkout is its
   * own sheet that creates the order + takes payment). */
  onOrderPlaced?: (orderId: string) => void
  /** Hide the "注文する" CTA. Delivery mode uses this so the cart sheet
   * becomes a pure review (items + quantities) while the actual order is
   * created inside DeliveryCheckoutSheet after address entry. */
  hideOrderButton?: boolean
  /** #1246: ゲストがカートを組んだ後、注文送信の瞬間だけログインを求める。
   * 呼び出し側 (StoreDetail) が postLoginRedirect の復帰先 (卓トークン付き)
   * を知っているため、遷移はコールバックで委譲する。未指定なら従来の
   * エラートースト挙動のまま。 */
  onRequireLogin?: () => void
  /**
   * メニューが古くなった時に親へ再取得を促す。
   *
   * カート投入後に店が売り切れにすると、送信が 400 で弾かれる。売り切れ表示を
   * 出し直さないと客は何が起きたのか分からず、押し続けても永久に成功しない。
   */
  onMenuStale?: () => Promise<void> | void
  /** #1353 放題プラン適用中の卓で 0 円になる商品の id。**サーバが 0 円を確定する**
   *  ので、ここは表示を実際の請求額に合わせるためだけに使う。 */
  allYouCanItemIds?: string[]
  /** #1531 セルフオーダー店 (卓 QR が入口)。卓番号なしのカートは送信させず、
   * 「お席の QR を読み取ってください」と案内する (サーバも 400 で弾く)。 */
  requireTable?: boolean
}

/**
 * Bottom sheet cart for self-order (#289).
 *
 * Sends the cart to the kitchen WITHOUT taking payment. The customer pays
 * later via the checkout flow (StoreDetail → "お会計" button → CheckoutSheet).
 *
 * Closing the sheet keeps the cart so the customer can come back to it.
 */
export default function CartSheet({ open, seatLabel, onClose, onOrderPlaced, hideOrderButton, onRequireLogin, onMenuStale, allYouCanItemIds, requireTable }: CartSheetProps) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const { cart, user, userId, updateCartQuantity, removeFromCart, clearCart } = useStore()
  const recoverRejectedCart = useCartRejectionRecovery(cart?.items ?? [], onMenuStale)
  // #1531 セルフオーダー店で卓 QR を通っていない (卓番号なし) カート
  const tableMissing = !!requireTable && !cart?.tableNumber

  const [submitting, setSubmitting] = useState(false)
  const [notes, setNotes] = useState('')

  if (!open || !cart) return null

  // 放題対象は 0 円で計算する。通常価格で合計を出すと、**客が見る金額と実際の
  // 請求額がズレる** (サーバは 0 円で確定する)。
  const covered = new Set(allYouCanItemIds || [])
  // #2031 放題が 0 円にするのは**商品の本体**。有料の選択 (メガジョッキ +200 円) は別料金なので残す。
  const priceOf = (i: { menuItemId: string; price: number; optionsPrice?: number }) =>
    covered.has(i.menuItemId) ? (i.optionsPrice ?? 0) : i.price
  const total = cart.items.reduce((s, i) => s + priceOf(i) * i.quantity, 0)

  // Preview the alias the store will see (mirrors backend logic in
  // routers/orders.py create_order). Lets the customer notice and tweak
  // the choice before the order is sent.
  // #2344 ゲストのときも実際に店へ出る番号を見せる。以前は 'Guest #XXXX' のまま
  // 出していたので、「XXXX という名前で表示される」と読めてしまっていた。
  // 番号は注文を作る側 (routers/orders.py _customer_alias) と同じ作り方 —
  // ログイン前はゲスト ID の末尾 4 桁。
  //
  // **ここでは発行しない** (閲覧しているだけの客に ID を配らない。発行は
  // 「厨房へ送信」の直前)。初めての客はまだ番号が無いので、伏字を出す代わりに
  // 「番号は注文を送るときに決まる」と言う — 無い番号をそれらしく見せない。
  const aliasSource = userId || getGuestId()
  const tagFallback = aliasSource
    ? `Guest #${aliasSource.split('_').pop()?.slice(-4).toUpperCase() ?? ''}`
    : null
  const pref = user?.displayPreference || 'tag'
  const previewAlias =
    pref === 'name' && user?.name
      ? user.name
      : pref === 'nickname' && user?.nickname
        ? user.nickname
        : tagFallback

  const submitOrder = async () => {
    // #1398 卓に着いた客はゲストのまま注文できる (支払いは店頭で完結する)。
    // 卓番号が無い = 卓QR を通っていないので、従来どおりログインを求める
    // — サーバも卓番号の無いゲスト注文は弾く。
    if (!userId && !cart.tableNumber) {
      if (onRequireLogin) {
        onRequireLogin()
        return
      }
      showToast({ type: 'error', message: t('cart.loginRequired') })
      return
    }
    // #1531 ログイン客でも、セルフオーダー店の卓番号なし注文は受けない
    // (どの席へ運ぶか分からない)。卓番号は署名付き卓 QR からしか入らない
    if (tableMissing) {
      showToast({ type: 'error', message: t('cart.tableRequired') })
      return
    }
    if (!cart.items.length || submitting) return
    // 送信の直前に発行する。閲覧しているだけの客に ID を配らないため。
    if (!userId) getOrCreateGuestId()
    setSubmitting(true)
    try {
      const res = await api.createSelfOrder(
        cart.storeId,
        toOrderItems(cart.items),
        // ゲストは userId を持たない。apiFetch が X-Guest-Id を添えるので、
        // legacy の ?user_id= は付けない (付けると別人の注文になる)。
        userId || undefined,
        { tableNumber: cart.tableNumber, notes: notes || undefined },
      )
      if (res?.detail || !res?.order) {
        const code = typeof res?.detail === 'object' ? res.detail?.code : undefined
        showToast({
          type: 'error',
          message: code === 'table_required'
            ? t('cart.tableRequired')
            : (typeof res?.detail === 'string' && res.detail) || t('cart.orderFailed'),
        })
        return
      }
      clearCart()
      setNotes('')
      onOrderPlaced?.(res.order.id)
    } catch (e) {
      const err = e as { code?: string; detail?: Record<string, unknown> }
      // 売り切れで弾かれた時は、**理由を出して復帰までやる**。
      // 以前は一律「注文の作成に失敗しました」で、どれが売り切れか分からず
      // カートも直らないため、押し続けても永久に成功しなかった。
      if (err?.code === 'items_sold_out') {
        const ids = (err.detail?.soldOutItemIds as string[] | undefined) ?? []
        const names = (err.detail?.soldOutItemNames as string[] | undefined) ?? []
        // #2031 売り切れは**商品**単位。同じ商品が 2 行 (塩 / たれ) あれば両方外す。
        cart.items
          .filter((i) => ids.includes(i.menuItemId))
          .forEach((i) => removeFromCart(cartLineOf(i)))
        // メニューを取り直して売り切れ表示を最新化する (画面に反映)
        await onMenuStale?.()
        showToast({
          type: 'error',
          message: names.length
            ? t('cart.soldOutRemoved', { names: names.join('、') })
            : t('cart.soldOutGeneric'),
        })
        return
      }
      // #1531 卓番号なし: UI 側でも止めているが、卓セッションが切れた等でサーバに
      // 弾かれる経路が残る。**apiFetch は 400 でも throw する**ので、上の res.detail
      // 分岐ではなくここで拾わないと「注文の作成に失敗しました」になってしまう。
      if (err?.code === 'table_required') {
        showToast({ type: 'error', message: t('cart.tableRequired') })
        return
      }
      // #2036 ご利用時間が終了した卓 (店が「時間で止める」を ON にしている場合のみ)。
      // 文言は席の帯の下に出している案内と同じものを使う — 画面の案内と手元のトーストで
      // 別のことを言うと、客は「自分が何かしたのか」と迷う。
      if (err?.code === 'table_stay_over') {
        showToast({ type: 'error', message: t('storeDetail.stayOverStopped') })
        return
      }
      // カートに入れた後で店のメニューが変わった (#2042 値段・商品の削除 / #2031 選択肢)。
      // カートを直してメニューを取り直す。値段が変わったときは**新しい金額を見てから**もう一度押してもらう。
      if (await recoverRejectedCart(e)) return
      // #1178 卓ロック: 会計開始済みの卓は追加注文不可 — 専用メッセージで案内
      showToast({
        type: 'error',
        message: err?.code === 'checkout_in_progress' ? t('checkout.tableLocked') : t('cart.orderFailed'),
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end justify-center bg-black/50 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-[430px] bg-white rounded-t-2xl p-6 pb-8 animate-slide-up max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 tap-effect"
          aria-label="close"
        >
          <X size={18} className="text-text-secondary" />
        </button>

        <h3 className="font-display font-semibold text-lg text-text-primary mb-1">
          {t('cart.title')}
        </h3>
        <p className="font-body text-xs text-text-tertiary mb-4">{cart.storeName}</p>

        <div className="space-y-3">
          {cart.items.map((item) => (
            <div key={cartLineOf(item)} className="flex items-center gap-3 rounded-xl border border-gray-100 p-3">
              {item.imageUrl && (
                <img src={item.imageUrl} className="w-14 h-14 rounded-lg object-cover shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                {/* #1857 客の言語が主・日本語が従。日本語で見ているときは 1 行。
                    英名だけを従にしていたので、タイ語等で見ていても日本語 + 英語だった。 */}
                <p className="font-body text-sm font-medium text-text-primary truncate">
                  {itemNameLines(item).primary}
                </p>
                {itemNameLines(item).secondary && (
                  <p className="font-jp text-xs text-text-tertiary truncate">
                    {itemNameLines(item).secondary}
                  </p>
                )}
                <VariationLine item={item} />
                <p className={`font-body text-sm font-bold mt-0.5 ${covered.has(item.menuItemId) ? 'text-matcha' : 'text-gold'}`}>
                  ¥{(priceOf(item) * item.quantity).toLocaleString()}
                  {covered.has(item.menuItemId) && (
                    <span className="ml-1 text-[10px] font-normal">{t('allYouCan.coveredBadge')}</span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => updateCartQuantity(cartLineOf(item), item.quantity - 1)}
                  className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 tap-effect"
                  aria-label="decrease"
                >
                  <Minus size={12} />
                </button>
                <span className="w-6 text-center font-body text-sm font-semibold">{item.quantity}</span>
                <button
                  onClick={() => updateCartQuantity(cartLineOf(item), item.quantity + 1)}
                  className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 tap-effect"
                  aria-label="increase"
                >
                  <Plus size={12} />
                </button>
                <button
                  onClick={() => removeFromCart(cartLineOf(item))}
                  className="ml-1 w-7 h-7 flex items-center justify-center rounded-full hover:bg-red-50 tap-effect"
                  aria-label="remove"
                >
                  <Trash2 size={12} className="text-yokoso-red/70" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {cart.tableNumber && (
          <p className="mt-4 rounded-lg bg-blue-50 border border-blue-100 p-2 text-xs font-body text-blue-700">
            {seatLabel || t('cart.tableNumber', { table: cart.tableNumber })}
          </p>
        )}

        <div className="mt-3 flex items-start justify-between gap-2 rounded-lg bg-gray-50 border border-gray-100 p-2.5">
          <p className="text-xs font-body text-text-secondary leading-relaxed">
            {previewAlias
              ? t('cart.displayAs', { alias: previewAlias })
              : t('cart.displayAsPending')}
          </p>
          <button
            type="button"
            onClick={() => {
              onClose()
              navigate('/profile')
            }}
            className="shrink-0 text-xs font-body text-navy underline underline-offset-2 tap-effect"
          >
            {t('cart.changeDisplayName')}
          </button>
        </div>

        <div className="mt-4">
          <label className="font-body text-xs text-text-tertiary">{t('cart.notesLabel')}</label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('cart.notesPlaceholder')}
            className="w-full mt-1 bg-gray-50 rounded-lg px-3 py-2.5 font-body text-sm text-text-primary outline-none focus:ring-2 focus:ring-yokoso-red/30"
            maxLength={200}
          />
        </div>

        <div className="mt-5 border-t border-gray-100 pt-4 flex items-center justify-between">
          <span className="font-body text-sm text-text-secondary">{t('cart.subtotal')}</span>
          <span className="font-display text-xl font-bold text-text-primary">¥{total.toLocaleString()}</span>
        </div>

        {!hideOrderButton && (
          <>
            {/* #1531 卓 QR を通っていないと注文できない。理由 (お料理をお持ちする
              * 席が分かるように) まで書かないと、客はただのエラーに見えて詰まる */}
            {tableMissing && (
              <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs font-body leading-relaxed text-amber-800">
                {t('cart.tableRequired')}
              </p>
            )}
            <p className="mt-2 text-xs font-body text-text-tertiary leading-relaxed">
              {t('cart.deferredPaymentNote')}
            </p>

            <button
              onClick={submitOrder}
              disabled={submitting || !cart.items.length || tableMissing}
              className={`mt-4 w-full h-14 rounded-xl font-display font-semibold text-base flex items-center justify-center gap-2 ${
                submitting || !cart.items.length || tableMissing
                  ? 'bg-gray-300 text-white'
                  : 'bg-yokoso-red text-white tap-effect active:scale-[0.98] transition-transform'
              }`}
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
              {t('cart.placeOrder')}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
